import React, { useState, useEffect } from "react";
import {
  Container, Row, Col, Card, CardBody, CardTitle, Input,
  Label, Button, Spinner, Modal, ModalHeader, ModalBody, Badge
} from "reactstrap";

// Import Editor
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

// Toastify for notifications
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

const LEGAL_SECTIONS = [
  { value: "KVKK", label: "KVKK Aydınlatma Metni" },
  { value: "PrivacyPolicy", label: "Gizlilik ve Çerez Politikası" },
  { value: "AdvertTerms", label: "İlan Verme ve Taşıma Hizmet Sözleşmesi" },
  { value: "Contract", label: "Platform Kullanım ve Araç Transfer Sözleşmesi" },
  { value: "DistanceSales", label: "Mesafeli Hizmet Sözleşmesi" },
  { value: "PreInformation", label: "Ön Bilgilendirme Formu" },
  { value: "CancellationRefund", label: "İptal ve İade Koşulları" },
  { value: "DeliveryConditions", label: "Teslimat Koşulları" },
  { value: "CookiePolicy", label: "Çerez Politikası" },
  { value: "PaymentSecurity", label: "Ödeme Güvenliği" },
];

const LegalDocuments = () => {
  document.title = "Yasal Metinler Yönetimi | LojGo Yönetim";

  // --- STATE TANIMLAMALARI ---
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal State'leri
  const [modal, setModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  // Form State'i
  const [formData, setFormData] = useState({
    Section_Type: "KVKK",
    Title: "",
    Description: "",
    Display_Order: 0,
    IsActive: true,
    Icon_Url: ""
  });

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const isAdmin = authUser?.Users_Type_ID === 3;

  // --- API İŞLEMLERİ ---

  // 1. Verileri Çek (GET)
  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await fetch("https://hiflowapi.stechomeyazilim.info/About_Us?$orderby=Display_Order asc");
      const result = await response.json();
      if (result && result.value) {
        const legalTypes = LEGAL_SECTIONS.map(s => s.value);
        const filtered = result.value.filter(item => legalTypes.includes(item.Section_Type));
        setItems(filtered);
      }
    } catch (error) {
      toast.error("Veriler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // 2. Silme İşlemi (DELETE)
  const handleDelete = async (id) => {
    if (window.confirm("Bu içeriği silmek istediğinize emin misiniz?")) {
      try {
        const response = await fetch(`https://hiflowapi.stechomeyazilim.info/About_Us(${id})`, {
          method: "DELETE"
        });
        if (response.ok) {
          toast.success("İçerik başarıyla silindi.");
          fetchItems();
        } else {
          toast.error("Silme işlemi başarısız oldu.");
        }
      } catch (error) {
        toast.error("Hata oluştu.");
      }
    }
  };

  // 3. Kaydetme / Güncelleme (POST / PUT)
  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.Title || !formData.Description) {
      toast.warning("Lütfen başlık ve açıklama alanlarını doldurun.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        Section_Type: formData.Section_Type,
        Title: formData.Title,
        Description: formData.Description,
        Display_Order: Number(formData.Display_Order) || 0,
        IsActive: formData.IsActive,
        Icon_Url: formData.Icon_Url || ""
      };

      let response;
      if (editId) {
        // Güncelleme
        response = await fetch(`https://hiflowapi.stechomeyazilim.info/About_Us(${editId})`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
      } else {
        // Yeni Kayıt
        response = await fetch("https://hiflowapi.stechomeyazilim.info/About_Us", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        toast.success(editId ? "İçerik güncellendi." : "İçerik eklendi.");
        toggleModal();
        fetchItems();
      } else {
        toast.error("Kaydetme işlemi başarısız oldu.");
      }
    } catch (error) {
      toast.error("Hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      setEditId(null);
      setFormData({
        Section_Type: "KVKK",
        Title: "",
        Description: "",
        Display_Order: 0,
        IsActive: true,
        Icon_Url: ""
      });
    }
  };

  const handleAddClick = () => {
    setEditId(null);
    setFormData({
      Section_Type: "KVKK",
      Title: "",
      Description: "",
      Display_Order: 0,
      IsActive: true,
      Icon_Url: ""
    });
    setModal(true);
  };

  const handleEditClick = (item) => {
    setEditId(item.ID);
    setFormData({
      Section_Type: item.Section_Type || "KVKK",
      Title: item.Title || "",
      Description: item.Description || "",
      Display_Order: item.Display_Order || 0,
      IsActive: item.IsActive ?? true,
      Icon_Url: item.Icon_Url || ""
    });
    setModal(true);
  };

  // Bölüm tiplerini okunabilir hale getiren yardımcı fonksiyon
  const getSectionBadge = (type) => {
    const section = LEGAL_SECTIONS.find(s => s.value === type);
    switch (type) {
      case "KVKK": return <Badge color="primary">{section?.label || type}</Badge>;
      case "PrivacyPolicy": return <Badge color="info">{section?.label || type}</Badge>;
      case "AdvertTerms": return <Badge color="warning">{section?.label || type}</Badge>;
      case "Contract": return <Badge color="success">{section?.label || type}</Badge>;
      case "DistanceSales": return <Badge color="primary">{section?.label || type}</Badge>;
      case "PreInformation": return <Badge color="info">{section?.label || type}</Badge>;
      case "CancellationRefund": return <Badge color="danger">{section?.label || type}</Badge>;
      case "DeliveryConditions": return <Badge color="warning">{section?.label || type}</Badge>;
      case "CookiePolicy": return <Badge color="dark">{section?.label || type}</Badge>;
      case "PaymentSecurity": return <Badge color="success">{section?.label || type}</Badge>;
      default: return <Badge color="secondary">{type}</Badge>;
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Kurumsal" breadcrumbItem="Yasal Metinler Yönetimi" />

          {/* ÜST BAŞLIK VE EKLE BUTONU */}
          <Row className="mb-4">
            <Col className="d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Yasal Metin ve Sözleşmeler</h4>
              {isAdmin && (
                <Button color="success" className="rounded-pill px-4" onClick={handleAddClick}>
                  <i className="mdi mdi-plus me-1"></i> Yeni İçerik Ekle
                </Button>
              )}
            </Col>
          </Row>

          {/* İÇERİK LİSTESİ */}
          <Row>
            {isLoading ? (
              <Col lg={12} className="text-center py-5">
                <Spinner color="primary" />
              </Col>
            ) : items.length === 0 ? (
              <Col lg={12}>
                <Card><CardBody className="text-center">Henüz yasal metin eklenmemiş.</CardBody></Card>
              </Col>
            ) : (
              items.map((item) => (
                <Col md={6} xl={6} key={item.ID} className="mb-4">
                  <Card className="h-100 shadow-sm border-0">
                    <CardBody className="d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center gap-2">
                          {getSectionBadge(item.Section_Type)}
                          {!item.IsActive && <Badge color="light" className="text-dark">Pasif</Badge>}
                        </div>
                        {isAdmin && (
                          <div className="d-flex gap-1">
                            <Button color="light" size="sm" onClick={() => handleEditClick(item)} className="p-1">
                              <i className="mdi mdi-pencil text-success font-size-16"></i>
                            </Button>
                            <Button color="light" size="sm" onClick={() => handleDelete(item.ID)} className="p-1">
                              <i className="mdi mdi-delete text-danger font-size-16"></i>
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="flex-grow-1 mb-3">
                        <h5 className="font-size-16 fw-bold mb-1">{item.Title}</h5>
                        <small className="text-muted">Sıra: {item.Display_Order}</small>
                      </div>

                      <div 
                        className="text-muted text-truncate" 
                        style={{ display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        dangerouslySetInnerHTML={{ __html: item.Description }} 
                      />
                    </CardBody>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </Container>
      </div>

      {/* EKLE / DÜZENLE MODALI */}
      <Modal isOpen={modal} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal} className="bg-light">
          {editId ? "İçeriği Düzenle" : "Yeni İçerik Ekle"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSave}>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label>Bölüm Türü</Label>
                  <Input
                    type="select"
                    value={formData.Section_Type}
                    onChange={(e) => setFormData({ ...formData, Section_Type: e.target.value })}
                  >
                    {LEGAL_SECTIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </Input>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label>Başlık</Label>
                  <Input
                    type="text"
                    required
                    placeholder="Örn: KVKK Aydınlatma Metni"
                    value={formData.Title}
                    onChange={(e) => setFormData({ ...formData, Title: e.target.value })}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label>Görünüm Sırası</Label>
                  <Input
                    type="number"
                    value={formData.Display_Order}
                    onChange={(e) => setFormData({ ...formData, Display_Order: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3 d-flex align-items-center h-100 pt-4">
                  <Input
                    type="checkbox"
                    checked={formData.IsActive}
                    onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })}
                    className="me-2"
                    style={{ width: "20px", height: "20px" }}
                  />
                  <Label className="mb-0">Aktif</Label>
                </div>
              </Col>
            </Row>

            <div className="mb-3">
              <Label>İçerik (HTML / Zengin Metin)</Label>
              <CKEditor
                editor={ClassicEditor}
                data={formData.Description}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setFormData((prev) => ({ ...prev, Description: data }));
                }}
                config={{
                  toolbar: [
                    'heading', '|',
                    'bold', 'italic', 'underline', 'strikethrough', '|',
                    'fontSize', 'fontColor', 'fontBackgroundColor', '|',
                    'alignment', '|',
                    'numberedList', 'bulletedList', '|',
                    'outdent', 'indent', '|',
                    'link', 'blockQuote', 'insertTable', '|',
                    'undo', 'redo'
                  ],
                  language: 'tr'
                }}
              />
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button color="light" onClick={toggleModal} type="button">
                İptal
              </Button>
              <Button color="primary" type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Kaydediliyor...
                  </>
                ) : (
                  "Kaydet"
                )}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default LegalDocuments;
